import { useQuery } from '@apollo/client';
import RequiresAuth from '@website/components/auth/RequiresAuth';
import CustomBreadcrumb from '@website/components/CustomBreadcrumb';
import LoadingPage from '@website/components/utilities/LoadingPage';
import { BookingForPayBalanceDocument } from '@website/types/graphqlOperations';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { Button, Col, Container } from 'react-bootstrap';
import Confetti from 'react-confetti';

type SuccessProps = {
  bookingID: string;
};

/**
 * Success message
 *
 * Shows after the form has been submitted
 */
export function SuccessMessage({ bookingID }: SuccessProps) {
  const { data, loading } = useQuery(BookingForPayBalanceDocument, {
    variables: { id: bookingID },
    fetchPolicy: 'network-only',
  });

  const booking = data?.booking;

  const linkToTripForm = useMemo(() => {
    if (booking?.spaces.filter((space) => space.active).length > 1) {
      return `/me/${booking?.user.id}/bookings/${booking?.id}/guests`;
    }
    const spaceId = booking?.spaces.filter((s) => s.active)[0]?.id;
    return `/me/${booking?.user.id}/guestForm/${spaceId}`;
  }, [booking?.id, booking?.spaces, booking?.user.id]);

  if (loading) return <LoadingPage />;

  return (
    <Col className="mx-auto">
      <Confetti
        initialVelocityX={8}
        initialVelocityY={8}
        numberOfPieces={400}
        gravity={0.2}
        recycle={false}
      />

      <p>
        Your payment has gone through successfully, and you're now fully paid
        up.
      </p>
      <p>
        <div className="d-grid gap-2">
          <Link
            href={`/me/${booking.user.id}/bookings/${booking.id}`}
            legacyBehavior
            passHref
          >
            <Button as="a" variant="primary">
              View booking
            </Button>
          </Link>
        </div>
      </p>
      <p className="fw-bold mt-5">1. Still need to complete your trip form?</p>
      <p>
        <div className="d-grid gap-2">
          <Link href={linkToTripForm} legacyBehavior passHref>
            <Button as="a" variant="primary" target="_blank">
              Complete Trip Form
            </Button>
          </Link>
        </div>
      </p>
      <p className="fw-bold">2. Still need to get travel insurance?</p>
      <p>
        Get travel insurance that will cover you for adventure activities. We
        recommend the adventure travel insurance specialist,{' '}
        <a href="https://riseandshield.com/" target="_blank" rel="noreferrer">
          Rise & Shield
        </a>{' '}
        - they cover 100s of activities and are easy to deal with if you need to
        make a claim.
      </p>
    </Col>
  );
}

export default function SuccessPage() {
  const {
    query: { bookingId, userID },
  } = useRouter();

  if (!bookingId) return <LoadingPage />;

  return (
    <RequiresAuth>
      <Container>
        <Col xs={12} sm={8} md={7} className="mx-auto">
          <CustomBreadcrumb
            items={[
              { text: 'Home', href: '/' },
              { text: 'Dashboard', href: `/me/${userID as string}` },
              {
                text: 'Booking',
                href: `/me/${userID as string}/bookings/${bookingId}`,
              },
              {
                text: 'Pay Balance',
                href: `/me/${userID as string}/bookings/${bookingId}/pay`,
              },
              { text: 'Success', active: true },
            ]}
          />

          <h1>Success!</h1>
          <SuccessMessage bookingID={bookingId as string} />
        </Col>
      </Container>
    </RequiresAuth>
  );
}
